/**
 * Tiny flat SVG icons. All 16×16, currentColor. Returns a fresh SVGElement
 * each call so the caller can append it to its own button without sharing
 * DOM nodes between rows.
 */

export type IconName =
  | 'eye'
  | 'eye-off'
  | 'copy'
  | 'close'
  | 'plus'
  | 'minus'
  | 'magnet'
  | 'audio'
  | 'background'
  | 'code'
  | 'font'
  | 'hand'
  | 'cursor'
  | 'lasso'
  | 'export'
  | 'import'
  | 'record'
  | 'skip-back'
  | 'share'
  | 'play'
  | 'pause'
  | 'kind-text'
  | 'kind-image'
  | 'kind-video'
  | 'kind-shape'
  | 'kind-drawing'
  | 'guide'
  | 'edit'
  | 'history';

// Static, code-controlled SVG bodies — no user input ever flows in.
const PATHS: Record<IconName, string> = {
  eye:
    '<path d="M1.5 8 C 3 4.5, 5.5 3, 8 3 S 13 4.5, 14.5 8 C 13 11.5, 10.5 13, 8 13 S 3 11.5, 1.5 8 Z" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="8" r="2" fill="currentColor"/>',
  'eye-off':
    '<path d="M2 2 L 14 14" stroke="currentColor" stroke-width="1.5"/><path d="M1.5 8 C 3 4.5, 5.5 3, 8 3 S 13 4.5, 14.5 8 C 13 11.5, 10.5 13, 8 13 S 3 11.5, 1.5 8 Z" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.55"/>',
  copy:
    '<rect x="5" y="3" width="8" height="10" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="3" y="5" width="8" height="8" rx="1.5" fill="var(--bg-elev,#1d1d22)" stroke="currentColor" stroke-width="1.5"/>',
  close:
    '<path d="M3.5 3.5 L 12.5 12.5 M 12.5 3.5 L 3.5 12.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  plus:
    '<path d="M8 3 V 13 M 3 8 H 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  minus:
    '<path d="M3 8 H 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  magnet:
    '<path d="M3 4 V 9 a 5 5 0 0 0 10 0 V 4 H 10 V 9 a 2 2 0 0 1 -4 0 V 4 Z" fill="currentColor"/><path d="M3 3 H 6 M 10 3 H 13" stroke="currentColor" stroke-width="1.6"/>',
  'kind-text':
    '<path d="M3 4 H 13 M 8 4 V 13 M 6 13 H 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  'kind-image':
    '<rect x="2" y="3" width="12" height="10" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="6" cy="7" r="1.2" fill="currentColor"/><path d="M2.5 12 L 6 9 L 9 11 L 12 7.5 L 13.5 9" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  'kind-video':
    '<rect x="2" y="3" width="12" height="10" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M7 6.2 L 10.5 8 L 7 9.8 Z" fill="currentColor"/>',
  'kind-shape':
    '<path d="M8 2 L 14 6 L 11.5 13 L 4.5 13 L 2 6 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>',
  'kind-drawing':
    '<path d="M3 11 L 11 3 L 13 5 L 5 13 Z" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M10 4 L 12 6" stroke="currentColor" stroke-width="1.5"/>',
  audio:
    '<path d="M3 6 V 10 H 6 L 10 13 V 3 L 6 6 Z" fill="currentColor"/><path d="M12 5 a 3 3 0 0 1 0 6" fill="none" stroke="currentColor" stroke-width="1.4"/>',
  background:
    '<path d="M3 3 H 11 V 9 H 3 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M11 5 H 13 V 8 a 1.2 1.2 0 0 1 -2.4 0" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M4 9 L 7 12 L 10 9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="7" cy="13.5" r="0.9" fill="currentColor"/>',
  code:
    '<path d="M5 4 L 2 8 L 5 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 4 L 14 8 L 11 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 3 L 7 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  font:
    '<path d="M3 13 L 6.5 3 H 9.5 L 13 13 M 4.6 9 H 11.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
  hand:
    '<path d="M5 8 V 4.5 a 1 1 0 0 1 2 0 V 8 M 7 4 V 3 a 1 1 0 0 1 2 0 V 8 M 9 4 V 3.5 a 1 1 0 0 1 2 0 V 8 M 11 5.5 a 1 1 0 0 1 2 0 V 10 a 4 4 0 0 1 -8 0 V 7 a 1 1 0 0 1 2 0" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>',
  cursor:
    '<path d="M3 2 L 12 7.5 L 8 9 L 7 13 Z" fill="currentColor"/>',
  lasso:
    '<rect x="3" y="3" width="10" height="10" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-dasharray="2 2"/>',
  export:
    '<path d="M8 2 V 10 M 5 7 L 8 10 L 11 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 11 V 13 a 1 1 0 0 0 1 1 H 12 a 1 1 0 0 0 1 -1 V 11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  import:
    '<path d="M8 2 V 10 M 5 5 L 8 2 L 11 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 10 V 13 a 1 1 0 0 0 1 1 H 12 a 1 1 0 0 0 1 -1 V 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  record:
    '<circle cx="8" cy="8" r="5" fill="#ff3b30"/>',
  share:
    '<circle cx="4.5" cy="8" r="2" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="11.5" cy="4" r="2" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="11.5" cy="12" r="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M6.3 7 L 9.7 5 M 6.3 9 L 9.7 11" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  'skip-back':
    '<rect x="3" y="3" width="2" height="10" fill="currentColor"/><path d="M13 3 L 5.5 8 L 13 13 Z" fill="currentColor"/>',
  play:
    '<path d="M4 3 L 13 8 L 4 13 Z" fill="currentColor"/>',
  pause:
    '<rect x="4" y="3" width="3" height="10" fill="currentColor"/><rect x="9" y="3" width="3" height="10" fill="currentColor"/>',
  guide:
    '<rect x="2" y="2" width="12" height="12" rx="1" fill="none" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2 2"/><path d="M8 2 V 14" stroke="currentColor" stroke-width="1.4"/><path d="M2 8 H 14" stroke="currentColor" stroke-width="1.4"/>',
  edit:
    '<path d="M2 14 L 4 10 L 12 2 L 14 4 L 6 12 Z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M10 4 L 12 6" stroke="currentColor" stroke-width="1.4"/>',
  history:
    '<path d="M3 8 a 5 5 0 1 0 1.5 -3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M2 3 L 2 6 L 5 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 5 V 8 L 10.5 9.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
};

const NS = 'http://www.w3.org/2000/svg';

// Parse the static path-body string once per call, transferring children
// into our svg element. We avoid innerHTML to keep CSP/static-analysis happy.
function parseBody(body: string): ChildNode[] {
  const doc = new DOMParser().parseFromString(`<svg xmlns="${NS}">${body}</svg>`, 'image/svg+xml');
  const root = doc.documentElement;
  return Array.from(root.childNodes);
}

export function icon(name: IconName, opts: { size?: number; title?: string } = {}): SVGSVGElement {
  const size = opts.size ?? 16;
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('class', 'icon');
  svg.setAttribute('aria-hidden', 'true');
  if (opts.title) {
    const t = document.createElementNS(NS, 'title');
    t.textContent = opts.title;
    svg.appendChild(t);
  }
  for (const child of parseBody(PATHS[name])) {
    svg.appendChild(child);
  }
  return svg;
}
